// 2026-05-26 - FormuLearning - 우측 스크롤 도트 인디케이터 (6 dots)
// 2026-06-28 - 스크롤 위치 자동 추적 (#24): 화면 상단에 위치한 스텝 도트만 점등
// 2026-09-24 수정 - 이슈 #59: Step 7 Final Report 도트 추가 (7 dots)
// 파일 위치: frontend/src/components/layout/ScrollStepper.tsx

import { useEffect } from 'react'
import { useUiStore } from '../../stores/uiStore'

const STEP_COLORS: Record<number, string> = {
  1: 'var(--color-accent-purple)',
  2: 'var(--color-accent-teal)',
  3: 'var(--color-accent-purple)',
  4: 'var(--color-accent-purple)',
  5: 'var(--color-accent-teal)',
  6: 'var(--color-accent-teal)',
  7: 'var(--color-accent-purple)',
}

// 도트 점등 기준: "화면에 보이는 Step 헤더"를 우선한다.
// 1순위 — 헤더가 화면에 보이는 Step. 여러 개 보이면 가장 위의 것.
//   (앞 Step 헤더가 위로 가려진 뒤 다음 Step 헤더가 아래에서 나타나면 곧바로 다음 도트로 전환)
// 2순위 — 헤더가 하나도 안 보이면(긴 Step 내부를 보는 중) 바로 위 Step(현재 들어와 있는 Step).
const HEADER_EPS = 8 // px — 헤더가 상단에 거의 닿은 상태도 "보임"으로 인정할 허용 오차

export default function ScrollStepper() {
  const { activeStep, setActiveStep } = useUiStore()

  // 스크롤·리사이즈 시 상단에 위치한 Step을 계산해 activeStep 갱신
  useEffect(() => {
    const container = document.getElementById('step-scroll-container')
    if (!container) return

    let raf = 0
    const compute = () => {
      raf = 0
      const rect = container.getBoundingClientRect()
      const cTop = rect.top
      const cBottom = rect.bottom
      let visibleStep = 0 // 헤더가 보이는 것 중 가장 위의 Step
      let lastAboveStep = 1 // 헤더가 상단 위로 가려진 마지막 Step(현재 들어와 있는 Step)
      for (let n = 1; n <= 7; n++) {
        const el = document.getElementById(`step-${n}`)
        if (!el) continue
        const top = el.getBoundingClientRect().top // 헤더 = 구역 최상단
        // 헤더가 화면에 보이는가 (상단에 거의 닿은 것도 인정)
        if (top >= cTop - HEADER_EPS && top < cBottom) {
          if (visibleStep === 0) visibleStep = n // 위→아래 순서이므로 첫 번째가 가장 위
        }
        if (top < cTop) lastAboveStep = n
      }
      const active = visibleStep || lastAboveStep
      // 불필요한 렌더 방지: 값이 바뀔 때만 반영
      if (useUiStore.getState().activeStep !== active) setActiveStep(active)
    }

    const onScroll = () => {
      if (raf) return
      raf = requestAnimationFrame(compute)
    }

    compute() // 초기 1회
    container.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      container.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [setActiveStep])

  const scrollToStep = (step: number) => {
    setActiveStep(step)
    const el = document.getElementById(`step-${step}`)
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div className="fixed right-4 top-1/2 z-40 flex -translate-y-1/2 flex-col gap-3">
      {[1, 2, 3, 4, 5, 6, 7].map((n) => (
        <button
          key={n}
          onClick={() => scrollToStep(n)}
          className="group relative flex items-center justify-center"
          title={`Step ${n}`}
        >
          <span
            className="block rounded-full transition-all"
            style={{
              width: activeStep === n ? 12 : 8,
              height: activeStep === n ? 12 : 8,
              backgroundColor: activeStep === n ? STEP_COLORS[n] : 'var(--color-text-secondary)',
              opacity: activeStep === n ? 1 : 0.4,
            }}
          />
        </button>
      ))}
    </div>
  )
}
